import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreateBooking } from '../../../hooks/bookings/useCreateBooking';
import { useGetSitterById } from '../../../hooks/clients/useGetSitterById';
import ClientHeader from '../ClientHeader/ClientHeader';
import AboutHomeCard from './components/AboutHomeCard';
import AboutSection from './components/AboutSection';
import AvailabilityCalendar from './components/AvailabilityCalendar';
import BookingModal, { BookingFormData } from './components/BookingModal';
import CancellationPolicy from './components/CancellationPolicy';
import LocationMap from './components/LocationMap';
import PhotoGallery from './components/PhotoGallery';
import ProfileHeader from './components/ProfileHeader';
import ReviewsList from './components/ReviewsList';
import ServicesCard from './components/ServicesCard';

const SitterProfile: React.FC = () => {
  const { sitterId } = useParams<{ sitterId: string }>();
  const navigate = useNavigate();

  const [bookingModalOpen, setBookingModalOpen] = useState(false);

  const { data: sitterData, isLoading } = useGetSitterById(sitterId);
  const { mutate: createBooking, isPending: isBookingPending } = useCreateBooking();

  const handleBack = () => {
    navigate('/dashboard');
  };

  const handleBook = () => setBookingModalOpen(true);

  const handleBookingConfirm = (bookingData: BookingFormData) => {
    createBooking(bookingData, {
      onSuccess: () => {
        setBookingModalOpen(false);
        // TODO: show success message
      },
      onError: (error) => {
        console.error('Failed to create booking:', error);
      },
    });
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#FAF8F5',
        }}
      >
        <ClientHeader />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 'calc(100vh - 64px)',
          }}
        >
          <Typography>Loading profile...</Typography>
        </Box>
      </Box>
    );
  }

  if (!sitterData) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#FAF8F5',
        }}
      >
        <ClientHeader />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 2,
            minHeight: 'calc(100vh - 64px)',
          }}
        >
          <Typography variant="h5">Sitter not found</Typography>
          <IconButton onClick={handleBack}>
            <ArrowBackIcon />
            <Typography sx={{ ml: 1 }}>Back to Search</Typography>
          </IconButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#FAF8F5',
        pb: 4,
      }}
    >
      {/* Client Header */}
      <ClientHeader />

      {/* Back Button */}
      <Container maxWidth="lg" sx={{ pt: 2 }}>
        <IconButton
          onClick={handleBack}
          sx={{
            mb: 2,
            '&:hover': {
              backgroundColor: 'rgba(23, 139, 142, 0.08)',
            },
          }}
        >
          <ArrowBackIcon sx={{ mr: 0.5 }} />
          <Typography sx={{ fontSize: '0.95rem' }}>Back to Search Results</Typography>
        </IconButton>
      </Container>

      <Container maxWidth="lg">
        {/* Profile Header */}
        <ProfileHeader sitter={sitterData} onBook={handleBook} />

        {/* Photo Gallery */}
        <PhotoGallery
          photos={sitterData.galleryImages}
          sitterName={`${sitterData.firstName} ${sitterData.lastName}`}
        />

        {/* Two Column Layout */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 3,
            mt: 3,
          }}
        >
          {/* Left Column - Main Content */}
          <Box sx={{ flex: { xs: '1', md: '0 0 65%' } }}>
            <AboutSection
              bio={sitterData.bio ?? 'No bio provided yet.'}
              sitterName={sitterData.firstName}
            />

            <AvailabilityCalendar
              availability={Object.fromEntries(
                sitterData.availability.map((a) => [a.date, a.isAvailable])
              )}
              serviceTypes={sitterData.serviceTypes}
            />

            <ReviewsList reviews={[]} sitterName={sitterData.firstName} />
          </Box>

          {/* Right Column - Sidebar */}
          <Box sx={{ flex: { xs: '1', md: '0 0 calc(35% - 24px)' } }}>
            <ServicesCard
              services={sitterData.pricing.map((p) => ({
                name: p.service,
                description: '',
                price: p.priceMin,
                unit: 'night',
              }))}
            />

            <AboutHomeCard
              sitterName={sitterData.firstName}
              acceptedPets={{
                preferences: sitterData.petPreferences ?? [],
              }}
              home={{
                hasFencedYard: sitterData.homeEnvironment?.includes('yard-with-fence') ?? false,
                allowsPetsOnFurniture:
                  sitterData.homeEnvironment?.includes('allowed-on-furniture') ?? false,
              }}
            />

            {sitterData.lat && sitterData.lng && (
              <LocationMap
                location={sitterData.username ?? sitterData.firstName}
                coordinates={{ lat: sitterData.lat, lng: sitterData.lng }}
              />
            )}

            <CancellationPolicy
              policy={{
                type: 'Flexible',
                description:
                  'Full refund if cancelled before 12:00 p.m. one day before the booking, 50% refund afterward.\n\nNo refund is payable if the booking is cancelled on or after the start date.',
              }}
            />
          </Box>
        </Box>
      </Container>
      <BookingModal
        open={bookingModalOpen}
        sitter={sitterData}
        onClose={() => setBookingModalOpen(false)}
        onConfirm={handleBookingConfirm}
        isLoading={isBookingPending}
      />
    </Box>
  );
};

export default SitterProfile;
